import moment from 'moment';
import { ORDER_TYPES } from '../helpers/constants';
import store from '../store';
import { isNeoPartner } from './helper';

/**
 * Class to handle medusa events
 */
class Medusa {
  url = process.env.REACT_APP_MEDUSA_URL || '';

  _sendEvent(payload) {
    return fetch(this.url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  _constructReqPayload(eventName, type, entityId, attrs) {
    const { auth } = store.getState();
    const eventsWithoutEmail = [
      'domain_marked_safe',
      'customer_marked_safe',
      'mail_account_marked_safe',
    ];

    return {
      eventTs: Date.now(),
      product: 'email',
      service: 'partner_panel',
      type,
      entityId,
      eventName,
      attrs: {
        ...attrs,
        actor: '',
        role: auth?.userRole,
        ...(eventsWithoutEmail.includes(eventName) && {
          actor_email: auth?.email,
        }),
      },
    };
  }

  getBundleAttrs() {
    const state = store.getState();
    const { bundle } = state.orders.orderDetails;
    return {
      bundle_id: bundle.id,
      bundle_status: bundle.status,
      domain_name: bundle.domainName,
      partner_id: bundle.partner.id,
    };
  }

  markDomainSafe(partnerId) {
    const state = store.getState();
    const orders = state.orders.orderDetails.orders;

    const orderType = isNeoPartner(partnerId)
      ? ORDER_TYPES.DOMAIN
      : ORDER_TYPES.MAIL;

    const domainOrder =
      orders.find((order) => order.type === orderType) || orders[0];

    return this._sendEvent(
      this._constructReqPayload(
        'domain_marked_safe',
        'flock_order',
        domainOrder.domainName,
        {
          domain_id: domainOrder.id,
          expired_on: moment(domainOrder.expiryDate).valueOf(),
        }
      )
    );
  }

  markCustomerSafe() {
    const state = store.getState();
    const { bundle, orders } = state.orders.orderDetails;
    return this._sendEvent(
      this._constructReqPayload(
        'customer_marked_safe',
        'titan_customer',
        bundle.customer.id,
        {
          customer_id: bundle.customer.id,
          order_count: orders.length,
        }
      )
    );
  }

  markMailAccountSafe(accountId) {
    return this._sendEvent(
      this._constructReqPayload(
        'mail_account_marked_safe',
        'flock_account',
        accountId,
        {
          account_id: accountId,
        }
      )
    );
  }

  markRenewalDateChanged(payload) {
    const {
      orderType,
      orderStatus,
      renewalDt,
      oldRenewalDt,
      reason,
      sourceHook,
      source,
    } = payload;
    const state = store.getState();
    const { bundle } = state.orders.orderDetails;
    return this._sendEvent(
      this._constructReqPayload(
        'pp_renewal_date_changed',
        'bundle',
        bundle.customer.id,
        {
          ...this.getBundleAttrs(),
          order_type: orderType,
          order_status: orderStatus,
          renewal_dt: renewalDt,
          old_renewal_dt: oldRenewalDt,
          reason: reason,
          source_hook: sourceHook,
          source: source,
        }
      )
    );
  }

  markPlanChanged(payload) {
    const {
      orderType,
      mailPlanType,
      sitePlanType,
      domainPlanType,
      oldMailPlanType,
      oldSitePlanType,
      oldDomainPlanType,
      renewalDt,
      oldRenewalDt,
      reason,
      sourceHook,
      source,
    } = payload;
    const state = store.getState();
    const { bundle } = state.orders.orderDetails;
    return this._sendEvent(
      this._constructReqPayload(
        'pp_plan_changed',
        'bundle',
        bundle.customer.id,
        {
          ...this.getBundleAttrs(),
          order_type: orderType,
          ...(mailPlanType && {
            mail_plan_type: mailPlanType,
            mail_old_plan_type: oldMailPlanType,
          }),
          ...(sitePlanType && {
            site_plan_type: sitePlanType,
            site_old_plan_type: oldSitePlanType,
          }),
          ...(domainPlanType && {
            neo_domain_plan_type: domainPlanType,
            neo_domain_old_plan_type: oldDomainPlanType,
          }),
          ...(renewalDt && {
            renewal_dt: renewalDt,
            old_renewal_dt: oldRenewalDt,
          }),
          reason: reason,
          source_hook: sourceHook,
          source: source,
        }
      )
    );
  }

  markMailboxLimitChanged(payload) {
    const {
      order_type,
      orderStatus,
      mailboxLimit,
      oldMailboxLimit,
      reason,
      sourceHook,
      source,
    } = payload;
    const state = store.getState();
    const { bundle } = state.orders.orderDetails;
    return this._sendEvent(
      this._constructReqPayload(
        'pp_mailbox_limit_changed',
        'bundle',
        bundle.customer.id,
        {
          ...this.getBundleAttrs(),
          order_type: order_type,
          order_status: orderStatus,
          mailbox_limit: mailboxLimit,
          old_mailbox_limit: oldMailboxLimit,
          reason: reason,
          source_hook: sourceHook,
          source: source,
        }
      )
    );
  }

  markRefundProcessed(payload) {
    const {
      refundFor,
      refundAction,
      totalRefundAmount,
      mailRefundAmount,
      siteRefundAmount,
      domainRefundAmount,
      reason,
      source,
    } = payload;
    const state = store.getState();
    const { bundle } = state.orders.orderDetails;
    return this._sendEvent(
      this._constructReqPayload(
        'pp_refund_processed',
        'bundle',
        bundle.customer.id,
        {
          ...this.getBundleAttrs(),
          refund_for: refundFor,
          refund_action: refundAction,
          total_refund_amount: totalRefundAmount,
          mail_refund_amount: mailRefundAmount,
          site_refund_amount: siteRefundAmount,
          neo_domain_refund_amount: domainRefundAmount,
          reason: reason,
          source: source,
        }
      )
    );
  }
}

const medusa = new Medusa();

export default medusa;
